/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewFilesDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/* global globalThis */
import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import { HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import { useDispatch } from "react-redux";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

const ViewFilesDialog = ({
  open,
  onClose,
  dialogTitle,
  selectedTestCaseData,
  setSelectedTestCaseData,
}) => {
  const dispatch = useDispatch();

  const [fileDataList, setFileDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileUrlsRef = useRef([]);

  const fetchFileBlob = useCallback(async (fileName) => {
    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...testCasesActions.get_files,
        params: {
          filename: fileName,
        },
        is_get_file: true,
      }),
    ));
    const url = globalThis.URL.createObjectURL(new Blob([resp]));

    return { fileName, url, error: false };
  }, [dispatch]);

  useEffect(() => {
    let isActive = true;
    const revokeUrls = (urls) => {
      for (const url of urls || []) {
        if (url) {
          globalThis.URL.revokeObjectURL(url);
        }
      }
    };

    const loadFiles = async () => {
      setLoading(true);
      const artifactLinks = selectedTestCaseData?.artefact_link || [];
      const responses = await Promise.allSettled(
        artifactLinks.map((name) => fetchFileBlob(name)),
      );
      const results = responses.map((response, index) => {
        if (response.status === "fulfilled") {
          return response.value;
        }
        return {
          fileName: artifactLinks[index],
          url: null,
          error: true,
        };
      });
      if (!isActive) {
        revokeUrls(results.map((item) => item?.url));
        return;
      }
      revokeUrls(fileUrlsRef.current);
      fileUrlsRef.current = results.map((item) => item?.url).filter(Boolean);
      setFileDataList(results);
      setLoading(false);
    };

    if (open && selectedTestCaseData) {
      loadFiles();
    } else {
      revokeUrls(fileUrlsRef.current);
      fileUrlsRef.current = [];
      setFileDataList([]);
      setLoading(false);
    }

    return () => {
      isActive = false;
      revokeUrls(fileUrlsRef.current);
      fileUrlsRef.current = [];
    };
  }, [open, selectedTestCaseData, fetchFileBlob]);

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={() => {
        onClose();
        setSelectedTestCaseData();
      }}
      title={dialogTitle || "View Files"}
    >
      <Stack
        spacing={2}
        sx={{ width: "600px", maxHeight: "80vh", overflowY: "auto" }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={400}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={4}>
            {fileDataList.map(({ fileName, url, error }) => (
              <Box
                key={fileName}
                border={1}
                borderColor="grey.300"
                borderRadius={2}
                p={2}
                overflow="hidden"
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ wordBreak: "break-word", fontWeight: 500 }}
                >
                  {fileName}
                </Typography>
                {error || !url ? (
                  <Typography color="error">Unable to load image</Typography>
                ) : (
                  <Box
                    component="img"
                    src={url}
                    alt={fileName}
                    sx={{
                      p: 2,
                      width: "100%",
                      maxHeight: 400,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                      backgroundColor: "#f9f9f9",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Stack>
    </CustomDialogBoxV2>
  );
};

ViewFilesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string,
  selectedTestCaseData: PropTypes.shape({
    artefact_link: PropTypes.arrayOf(PropTypes.string),
  }),
  setSelectedTestCaseData: PropTypes.func.isRequired,
};

export default ViewFilesDialog;
